/**
 * Base API client for Oscar.
 *
 * Wraps fetch with:
 *   - JSON body (de)serialisation
 *   - Same-origin /api/* calls (the SWA proxies these to func-oscar)
 *   - handleApiError-style classification: network vs auth (401/403) vs
 *     other HTTP failure — so callers can render the right toast.
 *
 * Per OnsiteCore-platform.md §9: never swallow errors; always re-throw an
 * ApiError so downstream code can decide how to handle it.
 */

export class ApiError extends Error {
  status: number;
  kind: 'network' | 'auth' | 'http';

  constructor(message: string, status: number, kind: ApiError['kind']) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.kind = kind;
  }
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

interface RequestOptions {
  method?: Method;
  body?: unknown;
  signal?: AbortSignal;
}

/**
 * Guards the expired-session re-login below.
 *
 * The page issues several API calls at once (TanStack Query fires its queries in
 * parallel), so an expired session produces several opaque redirects within
 * milliseconds. Each one used to call `window.location.assign`, giving a burst of
 * full-page navigations to the same endpoint — which the browser reports as
 * ERR_TOO_MANY_REDIRECTS.
 *
 * Worse, `returnTo` was read at the moment each call failed. By the time the second one
 * ran, the location could already BE `/.auth/login/aad?...`, so it re-encoded the login
 * URL into its own `post_login_redirect_uri` and nested indefinitely.
 *
 * One navigation per page load is all that can ever be correct: the page is leaving.
 */
let reauthNavigationStarted = false;

/** Where to send the user back to after signing in. Never a `/.auth/` path — that is
 *  how the login URL ends up nested inside its own redirect parameter. */
function safeReturnTo(): string {
  const here = window.location.pathname + window.location.search + window.location.hash;
  return window.location.pathname.startsWith('/.auth/') ? '/' : here;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = opts;
  let response: Response;

  try {
    response = await fetch(path, {
      method,
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'include',
      // Don't let fetch follow the SWA 401→AAD-login 302 across origins (it
      // CORS-fails and surfaces as a confusing "Failed to fetch"). With manual
      // redirects an expired session lands below as an opaque redirect instead.
      redirect: 'manual',
      signal,
    });
  } catch (err) {
    // fetch only throws on network-layer failures.
    throw new ApiError(
      err instanceof Error ? err.message : 'Network error',
      0,
      'network',
    );
  }

  // Expired SWA session: unauthenticated /api/* calls get a 302 → AAD login.
  // With redirect:'manual' that arrives here as an opaque redirect (status 0)
  // rather than a cross-origin CORS failure. Re-authenticate with a full-page
  // navigation — which re-establishes the SWA session cookie and returns the
  // user to where they were — instead of showing "Failed to fetch".
  if (response.type === 'opaqueredirect' || response.status === 0) {
    // Only the first failing call navigates — see reauthNavigationStarted. The rest
    // simply never resolve, because the page is on its way out either way.
    if (!reauthNavigationStarted) {
      reauthNavigationStarted = true;
      window.location.assign('/.auth/login/aad?post_login_redirect_uri=' + encodeURIComponent(safeReturnTo()));
    }
    return new Promise<never>(() => {}); // page is navigating away; never resolve
  }

  if (response.status === 401 || response.status === 403) {
    throw new ApiError(
      response.status === 401
        ? 'Not signed in'
        : 'Forbidden — you do not have permission for this action',
      response.status,
      'auth',
    );
  }

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const data = (await response.json()) as { error?: string };
      if (data?.error) message = data.error;
    } catch {
      // Response body wasn't JSON — keep the generic message.
    }
    throw new ApiError(message, response.status, 'http');
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const apiClient = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { method: 'GET', signal }),
  post: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'POST', body, signal }),
  patch: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'PATCH', body, signal }),
  put: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'PUT', body, signal }),
  delete: <T>(path: string, signal?: AbortSignal) =>
    request<T>(path, { method: 'DELETE', signal }),
};
